'use client';

import { useEffect } from 'react';

/**
 * Marks which form variant this page IS, in the URL, so Google Analytics can split funnel, form_start, generate_lead and the thank-you
 * conversion by variant with NO GA setup, and so lead emails / the admin table can show it (src/lib/form-variant.ts).
 *
 *   ?v=a  original form      ?v=b  new form      ?v=ta / ?v=tb  a tester who forced a variant with ?form=old|new (excluded from the report)
 *
 * The page passes its own truth (`variant`): the original page renders <VariantMarker variant="a" />, the new one variant="b". The only thing read
 * from the lp_demo_v cookie is whether this is a TEST visit ("-test" cookie, set by ?form=). So the marker can never disagree with the form shown.
 * It is applied TWICE on purpose: an inline script in the server HTML (before the page's own tags, so the first page_view already carries it), and a
 * client effect after hydration (Next resets the address bar when it hydrates, and pages that show a loader first never run an inline script that
 * React inserts later). It only rewrites the address bar (history.replaceState keeping Next's state); it never reloads.
 */
type V = 'a' | 'b';

function apply(variant: V) {
  try {
    const test = /(?:^|; )lp_demo_v=[a-z]+-test(?:;|$)/.test(document.cookie);
    const v = test ? 't' + variant : variant;
    const u = new URL(location.href);
    if (u.searchParams.get('v') !== v) {
      u.searchParams.set('v', v);
      history.replaceState(history.state, '', u.pathname + u.search + u.hash);
    }
  } catch { /* never break the page */ }
}

const inline = (variant: V) => `(function(){try{var t=/(?:^|; )lp_demo_v=[a-z]+-test(?:;|$)/.test(document.cookie);var v=t?'t${variant}':'${variant}';var u=new URL(location.href);if(u.searchParams.get('v')!==v){u.searchParams.set('v',v);history.replaceState(history.state,'',u.pathname+u.search+u.hash);}}catch(e){}})();`;

export function VariantMarker({ variant }: { variant: V }) {
  useEffect(() => apply(variant), [variant]);
  return <script dangerouslySetInnerHTML={{ __html: inline(variant) }} />;
}
